import {Trans} from '@lingui/macro';
import React, {FC, ReactNode, RefObject} from 'react';
import {animated, Spring} from 'react-spring';
import styled, {css} from 'styled-components';

import {Handler, HandlerOf, Renderer} from '../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {TooltipCoordinator} from '../../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {blendColors} from '../../../../../../libs/shared/ui/src/styles/colorHelpers';
import {fontSizes, fontWeights} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {ellipsis} from '../../../../../../libs/shared/ui/src/styles/mixins';
import {UserThemeConsumer} from '../../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {UserTheme} from '../../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {AbsoluteDateTimeFormatterContainer} from '../../../../../../srcCommon/formatters/dateTime/absoluteDateTimeFormatterContainer';
import {useToggle} from '../../../../../../srcCommon/helpers/hookHelpers';
import {
  searchableAttrsBody,
  searchableAttrsFrom,
} from '../../../../../../srcCommon/inboxes/search/searchHelpers';
import {CommentReactionVisualProps} from '../../../../../../srcCommon/inboxes/viewer/activity/comment/commentViewerSelectors';
import {markFullStorySensitive} from '../../../../../helpers/fullstoryHelpers';
import {buildTeammateDisplayName} from '../../../../../helpers/modelHelpers';
import {OnboardingTipTypesEnum} from '../../../../../models/api/preferencesModel';
import {TeammateModel} from '../../../../../models/api/teammateModel';
import {InteractiveDateTimeFormatterContainer} from '../../../../common/formatters/dateTime/interactiveDateTimeFormatterContainer';
import {InfoTipContainer} from '../../../../common/infoTips/infoTipContainer';
import {CommentReactions} from './commentBaseReactions';
import {CommentHeaderIcon, commentHeaderIconWidth} from './commentHeaderIcon';
import {CommentOperations} from './commentOperations';

/*
 * Props.
 */

interface CommentBaseProps {
  shouldHideTitle?: boolean;
  shouldHideReplyArrow?: boolean;
  shouldHideBackground?: boolean;
  shouldHideTimestamp?: boolean;
  shouldHideOperations?: boolean;
  shouldHideReactions?: boolean;
  renderTitle: Renderer;
  renderAvatar: Renderer;
  date?: number;
  lastEditedAt?: number;
  isFaded?: boolean;
  isFromUser?: boolean;
  isLastSendingOffline?: boolean;
  isNewUnread?: boolean;
  isUnread?: boolean;
  isVisible?: boolean;
  isVisibleOnlyToUser?: boolean;
  hasError?: boolean;
  deleteComment?: Handler;
  editComment?: Handler;
  retryComment?: Handler;
  canAddReactions?: boolean;
  reactions?: Array<CommentReactionVisualProps>;
  userId?: number;
  onToggleReaction?: HandlerOf<string>;
  onAddReaction?: HandlerOf<string>;
  onReplyClick?: Handler;
  isReply?: boolean;
  hasSameAuthorAsPrevious?: boolean;
  isReferencingTheSameMessageAsPrevious?: boolean;
  viewerRef?: RefObject<HTMLDivElement>;
  shouldShowDemoInboxTip?: boolean;
  isPinned?: boolean;
  pinnedByTeammate?: TeammateModel;
  togglePinComment?: Handler;
  interactionId?: string;
  shouldRenderWithoutBubble?: boolean;
  isFirstComment?: boolean;
  children?: ReactNode;
  wasJustPinned: boolean;
  wasJustHighlighted: boolean;
}

/*
 * Style.
 */

interface CommentStyleProps {
  $shouldHideTitle: boolean;
  $isPinned: boolean;
  $hasSameAuthorAsPrevious: boolean;
  $isReply: boolean;
  $isFirstComment: boolean;
}
export const StyledCommentDiv = styled.div<CommentStyleProps>`
  display: grid;
  grid-template-columns: 30px auto minmax(30%, 1fr);
  grid-column-gap: 12px;
  grid-auto-rows: auto;
  grid-template-areas:
    'comment comment comment'
    '. reactions .';
  align-items: center;
  justify-items: start;

  ${(p) => getMarginTopForStyledCommentDiv(p)};
`;

function getMarginTopForStyledCommentDiv(props: CommentStyleProps) {
  if (props.$hasSameAuthorAsPrevious && (props.$isPinned || props.$isReply)) {
    return css`
      margin-top: 10px;
    `;
  }

  if (!props.$shouldHideTitle) {
    if (props.$isFirstComment) {
      return css`
        margin-top: 7px;
      `;
    }

    return css`
      margin-top: 16px;
    `;
  }

  return css`
    margin-top: 3px;
  `;
}

export const StyledHeaderContentDiv = styled.div`
  grid-area: header;
  display: flex;
  flex-direction: column;
`;

export const StyledHeaderRowDiv = styled.div`
  margin-bottom: 4px;
  padding-left: 28px;
  margin-top: 3px;
  line-height: 16px;

  display: grid;
  grid-template-columns: ${commentHeaderIconWidth}px auto;
  grid-template-areas: 'header-icon title';
  grid-column-gap: 5px;
  align-items: center;
  position: relative;

  &:first-child {
    margin-top: 0px;
  }
`;

const StyledHeaderIconDiv = styled.div`
  grid-area: header-icon;
`;

export const StyledCommentHeaderTitleDiv = styled.div`
  grid-area: title;

  color: ${(p) => p.theme.greys.shade80};
  font-size: ${fontSizes.tiny};
  font-weight: ${fontWeights.semibold};
`;

const StyledPinnedByHeaderTitleDiv = styled(StyledCommentHeaderTitleDiv)`
  font-weight: ${fontWeights.semibold};
`;

interface AvatarStyleProps {
  $isFaded?: boolean;
}
const StyledAvatarDiv = styled.div<AvatarStyleProps>`
  grid-area: avatar;

  opacity: ${(p) => (p.$isFaded ? '0.5' : '1')};
`;

const StyledDemoInboxTipDiv = styled.div`
  position: absolute;
  right: -20px;
  top: 0px;
`;

const makeUnreadStyle = (theme: UserTheme) => ({
  backgroundColor: theme.palette.blue.shade20,
});

const makeJustPinnedStyle = (theme: UserTheme) => ({
  backgroundColor: theme.palette.blue.shade20,
});

const makeJustHighlightedStyle = (theme: UserTheme) => ({
  backgroundColor: theme.palette.blue.shade20,
});

const makeReadStyle = (theme: UserTheme) => ({
  backgroundColor: blendColors(theme.greys.shade20, theme.alphas.gray10),
});

const makeErrorStyle = (theme: UserTheme) => ({
  backgroundColor: theme.palette.red.shade20,
});

interface BubbleStyleProps {
  $isFaded?: boolean;
  $shouldHideBackground?: boolean;
}
const StyledBubbleDiv = styled.div<BubbleStyleProps>`
  display: grid;
  max-width: 100%;

  color: ${(p) => p.theme.greys.shade80};
  font-size: ${fontSizes.medium};
  font-weight: ${fontWeights.normal};
  height: 100%;

  ${(p) => maybeAddBubbleShape(p)};
  opacity: ${(p) => (p.$isFaded ? '0.5' : '1')};
`;
const AnimatedBubbleDiv = animated(StyledBubbleDiv);

function maybeAddBubbleShape(props: BubbleStyleProps) {
  if (props.$shouldHideBackground) {
    return '';
  }

  return css`
    border-radius: 15px;
  `;
}

export const StyledCommentContentDiv = styled.div`
  display: grid;
  grid-area: content;
  width: 100%;
  overflow: hidden;
  word-break: break-word;
`;

export const StyledRestDiv = styled.div`
  grid-area: rest;
  width: 100%;
  display: flex;
  min-width: 0;
  margin-left: 1px;
`;

const StyledStatusDiv = styled.div`
  grid-area: status;
  margin-bottom: 2px;
  margin-top: 3px;
  padding-left: 10px;
  line-height: 13px;

  color: ${(p) => p.theme.greys.shade70};
  font-size: ${fontSizes.tiny};
  font-weight: ${fontWeights.medium};
`;

interface CommentRowStyleProps {
  $shouldRenderWithoutBubble?: boolean;
}
export const StyledCommentRow = styled.div<CommentRowStyleProps>`
  display: grid;
  width: 100%;
  grid-area: comment;
  grid-column-gap: 8px;
  grid-auto-rows: auto;
  grid-template-areas:
    'header header header'
    'avatar content rest'
    '. content .'
    '. status status';
  align-items: center;
  justify-items: start;

  ${(p) => buildStyledCommentRowGridTemplateColumns(p)};
`;

function buildStyledCommentRowGridTemplateColumns(props: CommentRowStyleProps) {
  if (props.$shouldRenderWithoutBubble) {
    return css`
      grid-template-columns: 30px 75% auto;
    `;
  }

  return css`
    grid-template-columns: 30px auto minmax(30%, 1fr);
  `;
}
interface TimestampStyleProps {
  $shouldHideTimestamp?: boolean;
}
export const StyledCommentTimestampWrapperDiv = styled.div<TimestampStyleProps>`
  flex: 1 0 0;
  display: flex;
  justify-content: flex-end;

  font-size: ${fontSizes.tiny};
  color: ${(p) => p.theme.greys.shade70};
  line-height: 30px;

  opacity: ${(p) => (p.$shouldHideTimestamp ? '0' : '1')};
  ${StyledCommentRow}:hover & {
    opacity: 1;
  }

  ${ellipsis()}
`;

export const StyledCommentTimestampDiv = styled.div`
  margin-left: 6px;
  margin-right: 5px;
  text-transform: uppercase;
  flex-shrink: 0;
  ${ellipsis()}
`;

const StyledCommentEditedTimestampDiv = styled.div`
  font-style: italic;
  margin-left: 6px;
  overflow: hidden;
`;

interface OperationsStyleProps {
  $isReactionPickerOpen?: boolean;
}
const StyledOperationsDiv = styled.div<OperationsStyleProps>`
  display: none;

  ${StyledCommentRow}:hover & {
    display: flex;
  }

  ${(p) =>
    p.$isReactionPickerOpen &&
    css`
      display: flex;
    `}
`;

const StyledReactionsWrapperDiv = styled.div`
  grid-area: reactions;
  width: 100%;

  display: flex;
  flex-wrap: wrap;

  margin-top: 4px;
  margin-bottom: 4px;
`;

/*
 * Component.
 */

export const CommentBase: FC<CommentBaseProps> = (props) => {
  const {wasJustPinned, wasJustHighlighted} = props;
  const [isReactionPickerOpen, toggleReactionPickerOpen] = useToggle();

  return (
    <StyledCommentDiv
      $shouldHideTitle={Boolean(props.shouldHideTitle)}
      $isPinned={Boolean(props.isPinned)}
      $hasSameAuthorAsPrevious={Boolean(props.hasSameAuthorAsPrevious)}
      $isReply={Boolean(props.isReply)}
      $isFirstComment={Boolean(props.isFirstComment)}
      ref={props.viewerRef}
      data-testid={props.interactionId}
    >
      <StyledCommentRow $shouldRenderWithoutBubble={props.shouldRenderWithoutBubble}>
        {maybeRenderHeader(props)}
        {renderContent(props, wasJustPinned, wasJustHighlighted, props.children)}
        <StyledRestDiv>
          {maybeRenderOperations(props, isReactionPickerOpen, toggleReactionPickerOpen)}
          {maybeRenderTimestamp(props)}
        </StyledRestDiv>
        {maybeRenderStatus(props)}
      </StyledCommentRow>
      {!props.shouldHideReactions && props.reactions && props.reactions.length > 0 && (
        <StyledReactionsWrapperDiv>
          <CommentReactions
            canAddReactions={props.canAddReactions}
            reactions={props.reactions}
            userId={props.userId}
            onAddReaction={props.onAddReaction}
            onToggleReaction={props.onToggleReaction}
          />
        </StyledReactionsWrapperDiv>
      )}
    </StyledCommentDiv>
  );
};

/*
 * Helpers.
 */

function getInitialStyle(props: CommentBaseProps, theme: UserTheme) {
  if (props.isFromUser) {
    return undefined;
  }

  if (props.isNewUnread) {
    return makeUnreadStyle(theme);
  }

  return undefined;
}

function getCurrentStyle(
  props: CommentBaseProps,
  wasJustPinned: boolean,
  wasJustHighlighted: boolean,
  theme: UserTheme,
) {
  if (props.hasError) {
    return makeErrorStyle(theme);
  }

  if (wasJustHighlighted) {
    return makeJustHighlightedStyle(theme);
  }

  if (wasJustPinned) {
    return makeJustPinnedStyle(theme);
  }

  if (props.isFromUser) {
    return makeReadStyle(theme);
  }

  if (!props.isVisible && props.isNewUnread) {
    return makeUnreadStyle(theme);
  }

  if (props.isNewUnread) {
    return makeReadStyle(theme);
  }

  return makeReadStyle(theme);
}

function renderContent(
  props: CommentBaseProps,
  wasJustPinned: boolean,
  wasJustHighlighted: boolean,
  children?: ReactNode,
) {
  return (
    <StyledCommentContentDiv>
      {props.shouldRenderWithoutBubble
        ? children
        : renderAnimatedBubble(props, wasJustPinned, wasJustHighlighted, children)}
    </StyledCommentContentDiv>
  );
}

function renderAnimatedBubble(
  props: CommentBaseProps,
  wasJustPinned: boolean,
  wasJustHighlighted: boolean,
  children?: ReactNode,
) {
  // Don't animate hidden backgrounds.
  if (props.shouldHideBackground) {
    return (
      <StyledBubbleDiv
        $isFaded={props.isFaded}
        $shouldHideBackground={props.shouldHideBackground}
        className={markFullStorySensitive()}
        {...searchableAttrsBody}
      >
        {children}
      </StyledBubbleDiv>
    );
  }

  return (
    <UserThemeConsumer>
      {(theme) => {
        const initialStyle = getInitialStyle(props, theme);
        const currentStyle = getCurrentStyle(props, wasJustPinned, wasJustHighlighted, theme);
        const shouldDelay = !props.hasError && !props.isFromUser && props.isNewUnread && props.isVisible;
        return (
          <Spring
            from={initialStyle}
            to={currentStyle}
            delay={shouldDelay ? 2000 : 0}
            config={shouldDelay ? {duration: 5000} : undefined}
          >
            {(style) => (
              <AnimatedBubbleDiv
                $isFaded={props.isFaded}
                $shouldHideBackground={props.shouldHideBackground}
                className={markFullStorySensitive()}
                style={style}
                {...searchableAttrsBody}
              >
                {children}
              </AnimatedBubbleDiv>
            )}
          </Spring>
        );
      }}
    </UserThemeConsumer>
  );
}

function renderPinnedByText(pinnedByTeammate: TeammateModel, userId?: number) {
  return pinnedByTeammate.id === userId ? (
    <Trans id="comment-pinned-by-you">Pinned by you</Trans>
  ) : (
    <Trans id="comment-pinned-by-teammate">Pinned by {buildTeammateDisplayName(pinnedByTeammate)}</Trans>
  );
}

function maybeRenderHeader(props: CommentBaseProps) {
  return (
    <>
      {maybeRenderHeaderContent(props)}
      {maybeRenderAvatar(props)}
    </>
  );
}

// There's 2 lines of content here: Author and PinnedBy.
//
// Author has 2 states:
//    1. Normal name state: e.g. "Rocky Balboa"
//    2. isReply state: e.g. "Bob replied to Bill"
//
// The Author line is rendered above the PinnedBy line EXCEPT when the Author line is a reply.
function maybeRenderHeaderContent(props: CommentBaseProps) {
  return (
    <StyledHeaderContentDiv>
      {!props.isReply && maybeRenderAuthor(props)}
      {maybeRenderPinnedBy(props.pinnedByTeammate, props.userId)}
      {props.isReply && maybeRenderAuthor(props)}
    </StyledHeaderContentDiv>
  );
}

function maybeRenderPinnedBy(pinnedByTeammate?: TeammateModel, userId?: number) {
  if (!pinnedByTeammate) {
    return null;
  }

  return (
    <UserThemeConsumer>
      {(theme) => (
        <StyledHeaderRowDiv>
          <StyledHeaderIconDiv>
            <CommentHeaderIcon iconName="pinFilledTiny" backgroundColor={theme.palette.blue.shade40} />
          </StyledHeaderIconDiv>

          <StyledPinnedByHeaderTitleDiv>
            {renderPinnedByText(pinnedByTeammate, userId)}
          </StyledPinnedByHeaderTitleDiv>
        </StyledHeaderRowDiv>
      )}
    </UserThemeConsumer>
  );
}

function maybeRenderAvatar(props: CommentBaseProps) {
  if (props.shouldHideTitle) {
    return null;
  }

  return <StyledAvatarDiv $isFaded={props.isFaded}>{props.renderAvatar()}</StyledAvatarDiv>;
}

function maybeRenderAuthor(props: CommentBaseProps) {
  // If the previous comment is from the same author, nothing to do.
  const {renderTitle, shouldHideTitle} = props;
  if (shouldHideTitle) {
    return null;
  }

  // Otherwise, render the author name, or the replied to comment text.
  return (
    <UserThemeConsumer>
      {(theme) => (
        <StyledHeaderRowDiv {...searchableAttrsFrom}>
          {props.isReply && !props.shouldHideReplyArrow && (
            <StyledHeaderIconDiv>
              <CommentHeaderIcon iconName="replyTiny" backgroundColor={theme.greys.shade80} />
            </StyledHeaderIconDiv>
          )}
          <StyledCommentHeaderTitleDiv>{renderTitle()}</StyledCommentHeaderTitleDiv>
          {maybeRenderDemoInboxTip(props.shouldShowDemoInboxTip)}
        </StyledHeaderRowDiv>
      )}
    </UserThemeConsumer>
  );
}

function maybeRenderDemoInboxTip(shouldShowDemoInboxTip?: boolean) {
  // Only show the tip if this is a comment from the demo teammate in the demo conversation.
  if (!shouldShowDemoInboxTip) {
    return null;
  }

  return (
    <StyledDemoInboxTipDiv>
      <InfoTipContainer
        tipType={OnboardingTipTypesEnum.DEMO_INBOX_COMMENT}
        title={<Trans id="infotip-commentviewer-title">Front improves internal collaboration</Trans>}
        description={
          <Trans id="infotip-commentviewer-description">
            With comments and @mentions, loop in teammates to chime in on the message, so the entire
            discussion is saved in one thread.
          </Trans>
        }
        placement="top"
      />
    </StyledDemoInboxTipDiv>
  );
}

function maybeRenderOperations(
  props: CommentBaseProps,
  isReactionPickerOpen: boolean,
  toggleReactionPickerOpen: Handler,
) {
  const {
    isFromUser,
    hasError,
    retryComment,
    deleteComment,
    editComment,
    canAddReactions,
    reactions,
    onReplyClick,
    isPinned,
    togglePinComment,
    shouldHideOperations,
    isVisibleOnlyToUser,
  } = props;

  if (shouldHideOperations) {
    return null;
  }

  const shouldRenderReactions = canAddReactions && reactions && reactions.length === 0;
  if (!isFromUser) {
    return (
      <StyledOperationsDiv $isReactionPickerOpen={isReactionPickerOpen}>
        {shouldRenderReactions && (
          <CommentReactions
            isAddButtonOnly
            canAddReactions={canAddReactions}
            reactions={reactions}
            userId={props.userId}
            onAddReaction={props.onAddReaction}
            onToggleReaction={props.onToggleReaction}
            onTogglePicker={toggleReactionPickerOpen}
          />
        )}
        <CommentOperations
          replyToComment={onReplyClick}
          isPinned={isPinned}
          togglePinComment={togglePinComment}
          isVisibleOnlyToUser={isVisibleOnlyToUser}
        />
      </StyledOperationsDiv>
    );
  }

  return (
    <StyledOperationsDiv $isReactionPickerOpen={isReactionPickerOpen}>
      {reactions && !reactions.length && (
        <CommentReactions
          isAddButtonOnly
          canAddReactions={canAddReactions}
          reactions={reactions}
          userId={props.userId}
          onAddReaction={props.onAddReaction}
          onToggleReaction={props.onToggleReaction}
          onTogglePicker={toggleReactionPickerOpen}
        />
      )}
      <CommentOperations
        canRetry={hasError}
        retryComment={retryComment}
        editComment={editComment}
        deleteComment={deleteComment}
        replyToComment={onReplyClick}
        isPinned={isPinned}
        togglePinComment={togglePinComment}
        isVisibleOnlyToUser={isVisibleOnlyToUser}
      />
    </StyledOperationsDiv>
  );
}

function maybeRenderTimestamp(props: CommentBaseProps) {
  // If no date provided, nothing to do.
  const {date} = props;
  if (!date) {
    return null;
  }

  return (
    <StyledCommentTimestampWrapperDiv $shouldHideTimestamp={props.shouldHideTimestamp}>
      {maybeRenderEditedTimestamp(props)}
      <StyledCommentTimestampDiv>
        <InteractiveDateTimeFormatterContainer value={date} />
      </StyledCommentTimestampDiv>
    </StyledCommentTimestampWrapperDiv>
  );
}

function maybeRenderEditedTimestamp(props: CommentBaseProps) {
  const {lastEditedAt} = props;
  if (!lastEditedAt) {
    return undefined;
  }

  return (
    <TooltipCoordinator render={() => renderEditedTimestampTooltip(lastEditedAt)}>
      <StyledCommentEditedTimestampDiv>
        <Trans id="commentbase-edited-timestamp">(edited)</Trans>
      </StyledCommentEditedTimestampDiv>
    </TooltipCoordinator>
  );
}

function maybeRenderStatus(props: CommentBaseProps) {
  if (!props.isLastSendingOffline) {
    return undefined;
  }

  return (
    <StyledStatusDiv data-searchable>
      <Trans id="commentbase-queued-offline">Will be sent when back online</Trans>
    </StyledStatusDiv>
  );
}

function renderEditedTimestampTooltip(lastEditedAt: number) {
  const formattedDate = <AbsoluteDateTimeFormatterContainer value={lastEditedAt} />;
  return <Trans id="commentbase-edited-tooltip">Edited on {formattedDate}</Trans>;
}
